import { Box, Button } from '@mui/material';
import { styled } from "@mui/material/styles";
import { Ico_Filter_Active_Component, Ico_Filter_Component,Ico_SortAscending, Ico_SortDescending, useSnackbar, DeleteDialog } from '@sagecentral/core-ui';
import { FilterOp, FilterParam, GridMetaData, Sort } from 'models/grids';
import { GridBox, GridToolbar, GridNoRowsOverlay } from 'components/shared/grids';
import { DataGridPro, GridDensity, GridFilterItem, GridFilterModel, GridLogicOperator, GridPaginationModel, GridRowSelectionModel, GridSortItem, GridSortModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import useWorkItemPermissions from 'hooks/work-items/useWorkItemPermissions';
import UseWorkItemDraftColumns from './UseWorkItemDraftColumns';
import useWorkItemDrafts from 'hooks/work-items/useWorkItemDrafts';
import { customLocaleText } from 'models/grids/HeaderFilterLocaleText';
import useWorkItemDraftDelete from 'hooks/work-items/useWorkItemDraftDelete';

const NoRowsOverlay = () => {
    return (
        <GridNoRowsOverlay text={`No work item drafts found.`} />
    );
};

const StyledErrorButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.error.main,
    borderColor: theme.palette.error.main,
    color: 'white',
    '&:hover, &:active, &:focus': {
        backgroundColor: '#FF000020',
        color: theme.palette.error.main
    }
}));

const WorkItemDraftsGrid = () => {
    const apiRef = useGridApiRef();
    const workItemPermissions = useWorkItemPermissions();
    const [workItemIdToDelete, setWorkItemIdToDelete] = useState<{id: number, name: string}>({id: 0, name: ''});
    const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
    const { displaySnackbar } = useSnackbar();
    const [deleteDraft, isDeleting, deleteError] = useWorkItemDraftDelete(displaySnackbar, (workItemId) => {
        console.log(`Work item draft ${workItemId} deleted`);
    });


    const openDeleteWorkItem = (workItemId: number, workItemName: string) => {
        setWorkItemIdToDelete({id: workItemId, name: workItemName});
        setOpenDeleteConfirmation(true);
    }

    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [sortModel, setSortModel] = useState<GridSortModel>([{field: 'id', sort: 'desc'}]);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 100 });
    const [density, handleDensityChange] = useState<GridDensity | undefined>('standard');
    
    const buildGridMetadata = () => {
        const sort: Sort[] = [];
        const filter: FilterParam = { filters: [], logic: GridLogicOperator.And };
        if (sortModel && sortModel.length > 0) {
            sortModel.forEach((sm: GridSortItem) => {
                sort.push({
                    field: sm.field,
                    dir: sm.sort
                });
            });
        }
        sort.push(
            {
                field: 'id',
                dir: 'desc'
            }
        );
        sort.push(
            {
                field: 'name',
                dir: 'asc'
            }
        );
        if (filterModel && filterModel.items.length > 0) {
            filterModel.items.forEach((fm: GridFilterItem) => {
                filter.filters.push({
                    field: fm.field,
                    operator: fm.operator as FilterOp,
                    value: fm?.value?.length > 0 ? fm?.value : undefined
                });
            });
        }
        const metaData: GridMetaData = {
            page: paginationModel.page + 1,
            pageSize: paginationModel.pageSize,
            sort: sort,
            filterParam: filter
        };

        return metaData;
    };

    const [columns, visibilityModel, handleColumnVisibilityOrOrderChange] = UseWorkItemDraftColumns({
        hasViewAllWorkItems: workItemPermissions?.canAccessAllWorkItems ?? false,
        hasViewRelatedWorkItems: true,
        openDeleteWorkItem,
    });

    // const [deleteResult, isDeleting] = useWorkItemDelete(deleteWorkItemId, displaySnackbar);
    const gridMetadata = buildGridMetadata();

    const [ data, total, loading, error, refreshGrid] = useWorkItemDrafts(false, gridMetadata, true, false);

    const isFilterApplied = () => ((gridMetadata.filterParam?.filters.length || 0) > 0);
    const getData = () => (loading ? [] : data);


    const [includeHeaders, setIncludeHeaders] = useState(
        false,
    );
    const [includeOutliers, setExcludeOutliers] = useState(
        true,
    );
    const [outliersFactor, setOutliersFactor] = useState(
        String(10),
    );
    const [expand, setExpand] = useState(true);

    const deleteWorkItem = async () => {
        if (workItemIdToDelete.id) {
            await deleteDraft(workItemIdToDelete.id);
            refreshGrid()
            setOpenDeleteConfirmation(false);
        }
    };

    return (
        <>
            <GridBox getData={getData}>
                <Box sx={{ height: "80vh" }}>
                <DataGridPro
                        apiRef={apiRef}
                        rows={getData()}
                        rowCount={total}
                        columns={columns}
                        getRowId={(r) => r.id}
                        filterMode="server"
                        filterModel={filterModel}
                        sortingMode="server"
                        sortModel={sortModel}
                        paginationMode='server'
                        density={density ?? 'standard'}
                        onDensityChange={(newDensity) => handleDensityChange(newDensity)}
                        pagination={true}
                        pageSizeOptions={[25, 50, 75, 100]}
                        paginationModel={paginationModel}
                        initialState={{
                            columns: {
                                columnVisibilityModel: visibilityModel,
                            }
                        }}
                        onSortModelChange={(model) => setSortModel(model)}
                        onFilterModelChange={(model) => setFilterModel(model)}
                        onPaginationModelChange={(model) => setPaginationModel(model)}
                        onColumnVisibilityModelChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                        onColumnWidthChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                        onColumnOrderChange={() => handleColumnVisibilityOrOrderChange(apiRef)}
                        slots={{
                            columnMenuSortAscendingIcon: Ico_SortAscending,
                            columnMenuSortDescendingIcon: Ico_SortDescending,
                            columnSortedAscendingIcon: Ico_SortAscending,
                            columnSortedDescendingIcon: Ico_SortDescending,
                            columnUnsortedIcon: undefined,
                            openFilterButtonIcon: isFilterApplied() ? Ico_Filter_Active_Component : Ico_Filter_Component,
                            quickFilterIcon: Ico_Filter_Component,
                            columnFilteredIcon: Ico_Filter_Component,
                            quickFilterClearIcon: Ico_Filter_Component,
                            columnMenuFilterIcon: Ico_Filter_Component,
                            noRowsOverlay: () => NoRowsOverlay(),
                            toolbar: GridToolbar,
                        }}
                        headerFilters
                        localeText={customLocaleText}
                        slotProps={{panel: {placement: "bottom-end"}}}
                        loading={loading || !data}
                        getRowClassName={(params) =>
                            params.indexRelativeToCurrentPage % 2 === 0 ? 'row even' : 'row odd'
                        }
                        getRowHeight={() => 'auto'}
                    />
                </Box>
            </GridBox>
            <DeleteDialog nameOfItem={workItemIdToDelete.name}
                open={openDeleteConfirmation}
                onCancel={() => setOpenDeleteConfirmation(!openDeleteConfirmation)}
                onDelete={() => { deleteWorkItem() }}
            />
        </>
    )
};

export default WorkItemDraftsGrid;